import { css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';
import { t } from 'src/i18n';
import { LocalDate } from 'src/utilities/local-date.js';
import '../elements/d-smooth-resize';
import '../elements/d-action.js';
import '../elements/d-label.js';
import './d-list-header.js';
import { DListSection } from 'src/library/lists/d-list-section.js';
import type { TutorialParticipant } from './tutorial-participant';
import type { ListItemIcon } from './listItemIcon';
import type { ListSectionItemInput } from './listSectionItemInput';
import type { TutorialRegistrationDialogInput } from 'src/content/tutorials/tutorial-registration-dialog-input';
import type { TutorialRegistrationDialogResult } from 'src/content/tutorials/tutorial-registration-dialog-result';
import { TutorialRegistrationDialog } from 'src/content/tutorials/tutorial-registration-dialog';
import type { TutorialRegistration } from 'src/library/lists/tutorial-registration';

/**
 *
 */
@customElement('d-list-section-tutorial-participants')
export class DListSectionTutorialParticipants extends LitElement {
  static readonly styles = [
    ...DListSection.styles,
    css`
      :host {
        display: block;
      }
      d-action {
        margin-left: 0;
      }
      .registration-header {
        display: block;
        margin-left: var(--listPaddingLeft);
        padding: 12px 0 4px 0;
        font-weight: 500;
        color: var(--text-color);
      }
      .registration-header:first-child {
        padding-top: 8px;
      }
      .registration-header span {
        font-weight: normal;
      }
      .no-items {
        margin-top: 4px;
        margin-left: var(--listPaddingLeft);
      }
      d-wrap d-action {
        margin-left: -6px;
      }
      d-wrap > *:last-child {
        flex: none;
      }
    `,
  ];
  @property({ type: Number })
  tutorialId = 0;
  @property({ type: String })
  contentVersionId = '';
  @property({ type: String })
  tutorialName = '';
  @property({ type: String })
  versionLabel = '';
  @property({ type: String })
  token = '';
  @property({ type: Boolean, attribute: 'write-access', reflect: true })
  writeAccess = false;
  @property({ type: Boolean })
  currentVersion = false;
  @property({ type: Array })
  registrations: TutorialRegistration[] = [];
  @property({ type: Array })
  employees: TutorialParticipant[] = [];
  @property({ type: Number })
  organizationId = 0;
  @property({ type: Number })
  contentStickyTop = 0;
  @property({ type: String })
  today = LocalDate.now().toString();
  @property({ type: Boolean })
  opened = false;

  private participantsList(registration: TutorialRegistration): ListSectionItemInput[] {
    return registration.participants.map((e) => {
      let mainIcon: ListItemIcon | undefined = undefined;
      let secondaryLabel = t('tutorials.waitingForCompletion');
      if (e.completed) {
        const completedDate = LocalDate.fromString(e.completed);
        mainIcon = 'checkBlue';
        secondaryLabel = t('library.tutorialParticipants.completedOn', { date: completedDate.toStringForDisplay() });
      } else if (registration.dueDate) {
        const now = LocalDate.fromString(this.today);
        const dueDate = LocalDate.fromString(registration.dueDate);
        if (dueDate.isBefore(now)) {
          mainIcon = 'alert';
          secondaryLabel = t('library.tutorialParticipants.notCompletedInTime');
        }
      }
      return {
        label: e.name,
        accessible: false,
        secondaryLabel: secondaryLabel,
        mainIcon,
      };
    });
  }

  private completedText(registration: TutorialRegistration) {
    const completedCount = registration.participants.filter((r) => r.completed !== '').length;
    return t('library.tutorialParticipants.completedCount', {
      done: completedCount,
      total: registration.participants.length,
    });
  }

  protected async onRegisterParticipants() {
    const input: TutorialRegistrationDialogInput = {
      tutorialName: this.tutorialName,
      due: LocalDate.now().plusWeeks(2).toString(),
      employees: this.employees,
    };

    const result: TutorialRegistrationDialogResult = await TutorialRegistrationDialog.open(input);

    if (result.participants.length) {
      const detail = {
        tutorialId: this.tutorialId,
        contentVersionId: this.contentVersionId, // tutorialVersion is actually contentVersionId
        participants: result.participants,
        dueDate: result.due,
        comment: result.comment,
      };
      console.log('[Tutorial Registration] Creating registration:', detail);

      this.dispatchEvent(
        new CustomEvent<{
          tutorialId: number;
          contentVersionId: string;
          participants: TutorialParticipant[];
          dueDate: string;
          comment: string;
        }>('create-tutorial-registration', {
          bubbles: true,
          composed: true,
          detail,
        }),
      );
    }
  }

  renderRegistration(r: TutorialRegistration) {
    return html`
      <d-label
        class="registration-header"
        .label=${t('library.tutorialParticipants.registeredOn', {
          date: LocalDate.fromString(r.registrationDate).toStringForDisplay(),
        })}
        .sublabel=${r.dueDate
          ? t('library.tutorialParticipants.withDueDate', {
              date: LocalDate.fromString(r.dueDate).toStringForDisplay(),
            })
          : ''}
      ></d-label>
      <d-list-section no-header .items=${this.participantsList(r)}></d-list-section>
    `;
  }

  renderRegistrations() {
    if (this.opened) {
      return html`${this.registrations.map((r) => {
        return this.renderRegistration(r);
      })}`;
    }
    return html``;
  }

  renderHeaderAction() {
    if (this.currentVersion) {
      if (this.writeAccess) {
        return html`<d-action slot="top-right" @click=${() => this.onRegisterParticipants()}
          >${this.registrations.length === 0
            ? t('library.tutorialParticipants.register')
            : t('library.tutorialParticipants.newRegistration')}</d-action
        >`;
      }
      return html``;
    }
    return html`<d-action slot="top-right" @click=${() => (this.opened = !this.opened)}
      >${this.opened
        ? t('library.tutorialParticipants.hideParticipants')
        : t('library.tutorialParticipants.showParticipants')}</d-action
    >`;
  }

  render() {
    const listHeaderStyles = { top: this.contentStickyTop - 1 + 'px' };
    return html`
      <d-list-header
        icon="employees"
        .label=${t('library.tutorialParticipants.participants')}
        .sublabel=${this.versionLabel}
        style=${styleMap(listHeaderStyles)}
      >
        ${this.renderHeaderAction()}
      </d-list-header>
      ${this.registrations.length
        ? html`<d-smooth-resize>${this.renderRegistrations()}</d-smooth-resize>`
        : html`<d-label
            light
            label-action-plain
            class="no-items"
            .label=${t('library.tutorialParticipants.noRegistered')}
            .labelAction=${this.writeAccess && this.currentVersion
              ? t('library.tutorialParticipants.registerParticipants')
              : ''}
            @label-action=${() => this.onRegisterParticipants()}
          ></d-label>`}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-list-section-tutorial-participants': DListSectionTutorialParticipants;
  }
}
